import { Stack, Typography, useTheme } from "@mui/material";
import { CheckCircleIcon, RoundIcon } from "components";
import { STEP_ICON_MAP } from "./stepIconMap";
import type { DrawerStep } from "./types";

interface OnboardingDrawerStepItemProps {
  step: DrawerStep;
  isActive: boolean;
  isCompleted: boolean;
  isLocked?: boolean;
  onClick: () => void;
}

export const OnboardingDrawerStepItem = ({
  step,
  isActive,
  isCompleted,
  isLocked = false,
  onClick,
}: OnboardingDrawerStepItemProps) => {
  const theme = useTheme();
  const StepIcon = STEP_ICON_MAP[step.sidebarIcon];

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      gap="8px"
      onClick={onClick}
      aria-disabled={isLocked}
      sx={{
        padding: "8px",
        borderRadius: "8px",
        cursor: isLocked ? "not-allowed" : "pointer",
        opacity: isLocked ? 0.4 : 1,
        backgroundColor: isActive
          ? theme.palette.primitive.neutrals.neutral["900"]
          : "#2C303A",
        "&:hover": {
          backgroundColor: isLocked
            ? "#2C303A"
            : isActive
              ? theme.palette.primitive.neutrals.neutral["900"]
              : "rgba(255, 255, 255, 0.08)",
        },
        transition: "background-color 0.15s ease",
      }}
    >
      {isCompleted ? (
        <CheckCircleIcon
          sx={{
            color: theme.palette.success.main,
            width: "22px",
            height: "22px",
            flexShrink: 0,
          }}
        />
      ) : (
        <RoundIcon
          sx={{
            color: theme.palette.darkBg.low,
            width: "22px",
            height: "22px",
            flexShrink: 0,
          }}
        />
      )}

      {StepIcon && (
        <StepIcon
          sx={{
            color: isCompleted
              ? theme.palette.darkBg.medium
              : theme.palette.darkBg.high,
            width: "20px",
            height: "20px",
            flexShrink: 0,
          }}
        />
      )}

      <Stack gap={0} minWidth={0}>
        <Typography
          variant="body-small"
          sx={{
            color: theme.palette.darkBg.high,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            ...(isCompleted && { textDecoration: "line-through" }),
          }}
        >
          {step.sidebarTitle}
        </Typography>
        <Typography
          variant="body-xsmall"
          sx={{ color: theme.palette.darkBg.low }}
        >
          {step.estimatedTime}
        </Typography>
      </Stack>
    </Stack>
  );
};
